/**
 * Bangle Size Guide - Web Component
 * Interactive wrist measurement calculator with cm/inch toggle
 * and automatic size-chart row highlighting.
 *
 * Sizing strategy (aligned with Cartier / luxury standard):
 *   - ONE confident recommendation per measurement
 *   - Midpoint split within each range determines primary size
 *   - Edge cases near the midpoint get a subtle "could also fit" note
 *   - Never shows "out of range" or dual choices
 */
import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/* Size chart data
   Each entry: [minCm, maxCm, minIn, maxIn, smallerSize, largerSize]
   largerSize = null means only one size for that range.
   The midpoint of each cm range (X.5) determines which size
   is the primary recommendation. */
const SIZE_CHART = [
  [13, 13.9, 5.1, 5.4, 15, null],
  [14, 14.9, 5.5, 5.8, 15, 16],
  [15, 15.9, 5.9, 6.2, 16, 17],
  [16, 16.9, 6.3, 6.6, 17, 18],
  [17, 17.9, 6.7, 7.0, 18, 19],
  [18, 18.9, 7.1, 7.4, 19, 20],
  [19, 19.9, 7.5, 7.8, 20, 21],
];

/* How close to the midpoint (in cm) before we show "could also fit".
   0.3 means: 16.2-16.7 would show an alt note for the 16-16.9 range. */
const EDGE_THRESHOLD_CM = 0.3;
const EDGE_THRESHOLD_IN = 0.12;

class BangleSizeGuide extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.unit = 'cm';
  }

  /* Unit switching */
  setUnitCm() {
    this._setUnit('cm');
  }

  setUnitIn() {
    this._setUnit('in');
  }

  _setUnit(unit) {
    this.unit = unit;
    const isCm = unit === 'cm';

    this.refs.unitCm.classList.toggle('bsg__unit-btn--active', isCm);
    this.refs.unitIn.classList.toggle('bsg__unit-btn--active', !isCm);
    this.refs.unitCm.setAttribute('aria-pressed', String(isCm));
    this.refs.unitIn.setAttribute('aria-pressed', String(!isCm));

    const input = this.refs.wristInput;
    input.placeholder = isCm
      ? input.dataset.placeholderCm || 'e.g. 15.5'
      : input.dataset.placeholderIn || 'e.g. 6.1';

    if (input.value) {
      this.calculateSize();
    }
  }

  /**
   * Find the matching chart row. If the value falls outside
   * all ranges, return the nearest boundary row.
   */
  _findRow(raw) {
    const isInch = this.unit === 'in';

    const exact = isInch
      ? SIZE_CHART.find(([, , minIn, maxIn]) => raw >= minIn && raw <= maxIn)
      : SIZE_CHART.find(([minCm, maxCm]) => raw >= minCm && raw <= maxCm);

    if (exact) return exact;

    const first = SIZE_CHART[0];
    const minBound = isInch ? first[2] : first[0];
    if (raw < minBound) return first;

    const last = SIZE_CHART[SIZE_CHART.length - 1];
    const maxBound = isInch ? last[3] : last[1];
    if (raw > maxBound) return last;

    let closest = first;
    let minDist = Infinity;
    for (const row of SIZE_CHART) {
      const lo = isInch ? row[2] : row[0];
      const hi = isInch ? row[3] : row[1];
      const dist = Math.min(Math.abs(raw - lo), Math.abs(raw - hi));
      if (dist < minDist) {
        minDist = dist;
        closest = row;
      }
    }
    return closest;
  }

  /**
   * Determine the recommendation from a matched row.
   * Returns { primary: number, alt: number|null, altFit: 'closer'|'relaxed'|null }
   *
   * Logic:
   *   - Single-size row: always that size, no alt.
   *   - Dual-size row: midpoint split.
   *     Below midpoint -> smaller size is primary.
   *     At or above midpoint -> larger size is primary.
   *     Within EDGE_THRESHOLD of midpoint -> show alt.
   */
  _recommend(row, raw) {
    const [minCm, maxCm, minIn, maxIn, smaller, larger] = row;
    const isInch = this.unit === 'in';

    /* Single size row */
    if (larger === null) {
      return { primary: smaller, alt: null, altFit: null };
    }

    const lo = isInch ? minIn : minCm;
    const hi = isInch ? maxIn : maxCm;
    const mid = (lo + hi) / 2;
    const threshold = isInch ? EDGE_THRESHOLD_IN : EDGE_THRESHOLD_CM;
    const distToMid = Math.abs(raw - mid);
    const nearMidpoint = distToMid <= threshold;

    if (raw < mid) {
      /* Lower half: recommend smaller size */
      return {
        primary: smaller,
        alt: nearMidpoint ? larger : null,
        altFit: nearMidpoint ? 'relaxed' : null,
      };
    } else {
      /* Upper half (or exactly at midpoint): recommend larger size */
      return {
        primary: larger,
        alt: nearMidpoint ? smaller : null,
        altFit: nearMidpoint ? 'closer' : null,
      };
    }
  }

  /* Calculation */
  calculateSize() {
    const raw = parseFloat(this.refs.wristInput.value);
    if (isNaN(raw) || raw <= 0) return;

    const row = this._findRow(raw);
    const { primary, alt, altFit } = this._recommend(row, raw);
    const rowIdx = SIZE_CHART.indexOf(row);

    /* Show the result area (use CSS class, not hidden attr, to preserve aria-live) */
    this.refs.resultArea.classList.remove('bsg__result--hidden');

    /* Clear previous table highlights */
    const rows = this.refs.sizeTable.querySelectorAll('tbody tr');
    rows.forEach((r) => r.classList.remove('bsg__table-highlight'));

    /* Highlight the matching table row */
    if (rows[rowIdx]) {
      rows[rowIdx].classList.add('bsg__table-highlight');
      // WCAG 2.3.3 / 2.2.2 — honor the user's reduced-motion preference.
      // 'instant' jumps without animation; 'smooth' would force motion that
      // can trigger vestibular discomfort.
      rows[rowIdx].scrollIntoView({
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        block: 'nearest',
      });
    }

    /* Update the result display */
    this.refs.primarySize.textContent = `${primary} cm`;

    /* Shop CTA (2026-07): fill the size into the localized label and
       reveal — measuring should end in shopping. */
    if (this.refs.shopCta) {
      const ctaTpl = this.refs.shopCta.dataset.labelTpl || 'Shop bangles in [[size]]';
      this.refs.shopCta.textContent = ctaTpl.replace('[[size]]', `${primary} cm`);
      this.refs.shopCta.hidden = false;
    }

    /* Show or hide the "could also fit" note */
    if (alt !== null) {
      this.refs.altNote.hidden = false;
      const fitLabel = altFit === 'closer'
        ? this.refs.altNote.dataset.closerText || 'for a closer fit'
        : this.refs.altNote.dataset.relaxedText || 'for a more relaxed fit';
      this.refs.altSize.textContent = `${alt} cm`;
      this.refs.altFit.textContent = fitLabel;
    } else {
      this.refs.altNote.hidden = true;
    }

    /* Smooth-scroll to the result (respects prefers-reduced-motion) */
    this.refs.resultArea.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'nearest',
    });
  }
}

if (!customElements.get('bangle-size-guide')) {
  customElements.define('bangle-size-guide', BangleSizeGuide);
}
